import { useState } from 'react';
import { crmService } from '../../services/crmService';

// ─── Constants ─────────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: 'meeting',    label: 'Meeting',    icon: '🤝' },
  { value: 'phone_call', label: 'Call',       icon: '📞' },
  { value: 'email',      label: 'Email',      icon: '📧' },
  { value: 'demo',       label: 'Demo',       icon: '🎯' },
  { value: 'conference', label: 'Conference', icon: '🎪' },
  { value: 'site_visit', label: 'Site Visit', icon: '📍' },
  { value: 'video_call', label: 'VTC',        icon: '🖥' },
];

const SENTIMENT_OPTIONS = [
  { value: 'positive', label: 'Positive', color: '#10B981', dot: '🟢' },
  { value: 'neutral',  label: 'Neutral',  color: '#6B7280', dot: '⚪' },
  { value: 'negative', label: 'Negative', color: '#EF4444', dot: '🔴' },
  { value: 'mixed',    label: 'Mixed',    color: '#3B82F6', dot: '🔵' },
];

const today = () => new Date().toISOString().split('T')[0];

// ─── Shared styles ─────────────────────────────────────────────────────────────

const inp = {
  width: '100%', background: '#0A0F1C', border: '1px solid #1E2D45',
  borderRadius: 6, padding: '8px 10px', color: '#E2E8F0', fontSize: 13,
  boxSizing: 'border-box', outline: 'none',
};

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 600,
        marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase',
      }}>
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function InteractionModal({ contact, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [form, setForm] = useState({
    interaction_type: 'meeting',
    date:             today(),
    subject:          '',
    summary:          '',
    sentiment:        'neutral',
    next_action:      '',
    next_action_date: '',
    opportunity_id:   '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.date)           { setError('Date is required'); return; }
    if (!form.subject.trim()) { setError('Subject is required'); return; }
    setSaving(true);
    setError(null);
    try {
      await crmService.createInteraction({
        contact_id:       contact.id,
        type:             form.interaction_type,
        interaction_date: form.date,
        subject:          form.subject.trim(),
        summary:          form.summary.trim() || null,
        sentiment:        form.sentiment,
        next_steps:       form.next_action.trim() || null,
        next_action_date: form.next_action_date || null,
        opportunity_id:   form.opportunity_id ? parseInt(form.opportunity_id) : null,
      });
      onSuccess();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const displayName = (() => {
    const prefix = contact.rank_code || contact.grade_code || '';
    return `${prefix ? prefix + ' ' : ''}${contact.first_name} ${contact.last_name}`;
  })();

  const opportunities = contact?.opportunities || [];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#111827', border: '1px solid #1E2D45', borderRadius: 10,
        width: Math.min(560, window.innerWidth - 32),
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #1E2D45', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 700, color: '#F1F5F9', fontSize: 15 }}>Log Interaction</div>
            <div style={{ color: '#64748B', fontSize: 12, marginTop: 2 }}>
              {displayName}
              {contact.org_acronym && ` · ${contact.org_acronym}`}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: 22, lineHeight: 1,
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {/* Type + Date row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0 16px' }}>
            <Field label="Type" required>
              <select style={inp} value={form.interaction_type} onChange={e => set('interaction_type', e.target.value)}>
                {TYPE_OPTIONS.map(t => (
                  <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Date" required>
              <input type="date" style={inp} value={form.date} onChange={e => set('date', e.target.value)} />
            </Field>
          </div>

          <Field label="Subject" required>
            <input
              style={inp}
              value={form.subject}
              onChange={e => set('subject', e.target.value)}
              placeholder="Brief subject line"
              autoFocus
            />
          </Field>

          <Field label="Summary">
            <textarea
              style={{ ...inp, height: 88, resize: 'vertical' }}
              value={form.summary}
              onChange={e => set('summary', e.target.value)}
              placeholder="What was discussed?"
            />
          </Field>

          {/* Sentiment inline radios */}
          <Field label="Sentiment">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SENTIMENT_OPTIONS.map(s => {
                const active = form.sentiment === s.value;
                return (
                  <label
                    key={s.value}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                      padding: '6px 12px', borderRadius: 6,
                      border: `1px solid ${active ? s.color : '#1E2D45'}`,
                      background: active ? s.color + '22' : 'transparent',
                      fontSize: 12, color: active ? s.color : '#64748B',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="radio" name="sentiment" value={s.value}
                      checked={active} onChange={() => set('sentiment', s.value)}
                      style={{ display: 'none' }}
                    />
                    {s.dot} {s.label}
                  </label>
                );
              })}
            </div>
          </Field>

          {/* Next action row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '0 16px' }}>
            <Field label="Next Action">
              <input
                style={inp}
                value={form.next_action}
                onChange={e => set('next_action', e.target.value)}
                placeholder="What needs to happen next?"
              />
            </Field>
            <Field label="Due Date">
              <input type="date" style={inp} value={form.next_action_date} onChange={e => set('next_action_date', e.target.value)} />
            </Field>
          </div>

          {/* Link to opportunity */}
          {opportunities.length > 0 && (
            <Field label="Link to Opportunity">
              <select style={inp} value={form.opportunity_id} onChange={e => set('opportunity_id', e.target.value)}>
                <option value="">— none —</option>
                {opportunities.map(op => (
                  <option key={op.id} value={op.id}>{op.name}</option>
                ))}
              </select>
            </Field>
          )}

          {error && (
            <div style={{ color: '#EF4444', fontSize: 12, marginTop: -6, marginBottom: 10 }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #1E2D45',
          display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', color: '#94A3B8', border: '1px solid #374151',
              borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontSize: 13,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              background: '#22D3EE', color: '#0A0F1C', border: 'none',
              borderRadius: 6, padding: '8px 20px', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Log Interaction'}
          </button>
        </div>
      </div>
    </div>
  );
}
